import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { MRT_ColumnDef } from 'material-react-table';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import { useApi } from '../../../../../../shared/hooks/useApi';
import { transformProductData } from '../../../../tc-models/tc-req-dto/tc-component-details.req_dto';
import { Option } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useParams } from 'react-router-dom';
import { TCAppConfig } from '../../../../../../shared/app-configs/tc-app-configs';
import { ProductShipmentResponse } from '../../certified-products/certified-products.model';

interface TCComponentInformation {
  tcProductComponentOrder: string;
  tcProductComponentNo: string;
  tcComponentDetail: string;
  tcComponentInfoTrimsExcluded: string;
  tcComponentInfoAssociatedTc: string;
  tcComponentInfoFiberLength: string;
  tcComponentInfoYarnCount: string;
  tcComponentInfoFabricWeightAndConstruct: string;
  tcComponentInfoFabricWeightOfFinishedProduct: string;
  tcComponentNetShippingWeight: number | null;
  tcComponentSupplementaryWeight: number | null;
  tcComponentCertifiedAndNonCertifiedWeight: number | null;
  tcComponentInfoAdditional: string;
}

interface ComponentInfoProps {
  formRef: React.Ref<any>;
  componentData: TCComponentInformation;
  setComponentData: (data: TCComponentInformation) => void;
}

const ComponentInfo: React.FC<ComponentInfoProps> = ({ formRef, componentData, setComponentData }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState<TCComponentInformation>(componentData);
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const { loading, error, executeGet } = useApi();
  const { TE_ID } = useParams()
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };
  const fetchProductDetails = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST, { tcId: TE_ID });
      console.log(response?.data)
      const result = (response?.data.response as ProductShipmentResponse[])?.map((detail) => {console.log(detail); return({
        value: detail.tcProductNo,
        label: detail.tcProductNo,
      })}) || [];
      const uniqueData = result.filter((item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
      );
      setProductOptions(uniqueData)
      //  const result=payloadToMap(response);
      //  setData(result.data);
    } catch (err) {
      console.error('Failed to fetch TC Base Details:', err);
    }
  }
  useEffect(() => {
    TE_ID && fetchProductDetails()
  }, [])

  const resetForm = () => {
    setEditInitialValues(componentData);
  };

  const handleSubmit = (values: TCComponentInformation) => {
    setComponentData(values);
    toggleDrawer();
  };

  const computeCertifiedWeight = (net: number | string | null | undefined, supplementary: number | string | null | undefined): number | null => {
    const n = Number(net);
    const s = Number(supplementary);
    if (net === null || net === undefined || net === '' || supplementary === null || supplementary === undefined || supplementary === '' || isNaN(n) || isNaN(s)) {
      return null;
    }
    const result = n - s;
    return Number.isInteger(result) ? result : Number(result.toFixed(5));
  };

  const setCertifiedWeightFromValues = (
    net: number | string | null | undefined,
    supplementary: number | string | null | undefined,
    setFieldValue: (f: string, v: number | null | string) => void
  ) => {
    const isEmpty = (v: unknown) => v === null || v === undefined || v === '';
    if (isEmpty(net) || isEmpty(supplementary)) {
      setFieldValue('tcComponentCertifiedAndNonCertifiedWeight', '');
      return;
    }
    const computed = computeCertifiedWeight(net, supplementary);
    setFieldValue('tcComponentCertifiedAndNonCertifiedWeight', computed === null ? '' : computed);
  };

  const initialValuesWithComputed = {
    ...componentData,
    tcComponentNetShippingWeight: componentData.tcComponentNetShippingWeight ?? '',
    tcComponentSupplementaryWeight: componentData.tcComponentSupplementaryWeight ?? '',
    tcComponentCertifiedAndNonCertifiedWeight: computeCertifiedWeight(
      componentData.tcComponentNetShippingWeight,
      componentData.tcComponentSupplementaryWeight
    ) ?? '',
  };

  const validationSchema = Yup.object().shape({
    tcProductComponentNo: Yup.string()
      .required("Component No. is required")
      .test('not-invalid-value', 'Component No. cannot be "n/a, N/A, NULL, null, or Not available"', 
        function(value) {
          if (!value) return true; 
          const trimmedValue = value.trim().toLowerCase();
          const invalidValues = ['n/a', 'n\\a', 'null', 'not available', 'na', 'nil'];
          const isInvalid = invalidValues.some(invalid => trimmedValue === invalid);
          return !isInvalid;
        }),
    tcComponentDetail: Yup.string().required("Component Detail is required"),
    tcComponentInfoTrimsExcluded: Yup.string().nullable(),
    tcComponentInfoAssociatedTc: Yup.string().nullable(),
    tcComponentInfoFiberLength: Yup.string().nullable(),
    tcComponentInfoYarnCount: Yup.string().nullable(),
    tcComponentInfoFabricWeightAndConstruct: Yup.string().nullable(),
    tcComponentInfoFabricWeightOfFinishedProduct: Yup.string().nullable(),
    tcComponentNetShippingWeight: Yup.mixed()
      .required("Component Net Shipping Weight is required")
      .test('required-not-empty', 'Component Net Shipping Weight is required', (value) => value !== null && value !== undefined && value !== '')
      .test(
        'is-exactly-five-decimal-places',
        'Number must be a valid decimal number with upto 5 decimal places.',
        (value: string | number | null | undefined) => {
          if (value === null || value === undefined || value === '') return true;
          return /^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(String(value));
        }
      )
      .test('min-zero', 'Value cannot be negative', (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === '') return true;
        const n = Number(value);
        return !isNaN(n) && n >= 0;
      })
      .typeError("Component Net Shipping Weight must be a valid number"),
    tcComponentSupplementaryWeight: Yup.mixed()
      .required("Component Supplementary Weight is required")
      .test(
        "required-not-empty",
        "Component Supplementary Weight is required",
        (value) => value !== null && value !== undefined && value !== ""
      )
      .test(
        "is-exactly-five-decimal-places",
        "Number must be a valid decimal number with upto 5 decimal places.",
        (value: string | number | null | undefined) => {
          if (value === null || value === undefined || value === "") return true;
          return /^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(
            String(value)
          );
        }
      )
      .test(
        "min-zero",
        "Value cannot be negative",
        (value: string | number | null | undefined) => {
          if (value === null || value === undefined || value === "") return true;
          const n = Number(value);
          return !isNaN(n) && n >= 0;
        }
      )
      .test(
        "supp-not-greater-than-net",
        "Component Supplementary Weight cannot be greater than Component Net Shipping Weight",
        function (value: string | number | null | undefined) {
          if (value === null || value === undefined || value === "") return true;
          const net = this.parent.tcComponentNetShippingWeight;
          if (net === null || net === undefined || net === "") return true;
          const nNet = Number(net);
          const nSupp = Number(value);
          if (isNaN(nNet) || isNaN(nSupp)) return true;
          return nSupp <= nNet;
        }
      )
      .typeError("Component Supplementary Weight must be a valid number"),
    tcComponentCertifiedAndNonCertifiedWeight: Yup.string().nullable(),
    tcComponentInfoAdditional: Yup.string().nullable(),
  });

  const formConfig = {
    // pageName: "component-details",
    initialValues: initialValuesWithComputed,
    validationSchema: validationSchema,
    onValuesChange: (values: Record<string, any>) => {
      setComponentData(values as TCComponentInformation);
    },
    sections: [
      {
        fields: [
          {
            label: 'Component Number',
            name: 'tcProductComponentNo',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: true,
            value: componentData.tcProductComponentNo,
            props: {
              placeholder: 'Enter Component Number',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Detail',
            name: 'tcComponentDetail',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: true,
            value: componentData.tcComponentDetail,
            props: {
              placeholder: 'Enter Component Detail',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Net Shipping\nWeight(kg)',
            name: 'tcComponentNetShippingWeight',
            type: 'text',
            gridSize: { xs: 12, md: 4},
            required: true,
            value: componentData.tcComponentNetShippingWeight,
            props: {
              type: 'number',
              placeholder: 'Enter Net Shipping Weight',
              variant: 'outlined',
              fullWidth: true,
              inputProps: { min: 0, step: '0.00001' },
            },
            events: {
              onChange: (updatedValue: unknown, values: TCComponentInformation, setFieldValue: (f: string, v: number | null | string) => void) => {
                const net = updatedValue as number | string | null | undefined;
                const supplementary = values.tcComponentSupplementaryWeight;
                setCertifiedWeightFromValues(net, supplementary, setFieldValue);
              },
            },
          },
          {
            label: 'Component Supplementary\nWeight(kg)',
            name: 'tcComponentSupplementaryWeight',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: componentData.tcComponentSupplementaryWeight,
            props: {
              type: 'number',
              placeholder: 'Enter Supplementary Weight',
              variant: 'outlined',
              fullWidth: true,
              inputProps: { min: 0, step: '0.00001' },
            },
            events: {
              onChange: (updatedValue: unknown, values: TCComponentInformation, setFieldValue: (f: string, v: number | null | string) => void) => {
                const net = values.tcComponentNetShippingWeight;
                const supplementary = updatedValue as number | string | null | undefined;
                setCertifiedWeightFromValues(net, supplementary, setFieldValue);
              },
            },
          },
          {
            label: 'Component Certified + Non-Certified\nWeight(kg)',
            name: 'tcComponentCertifiedAndNonCertifiedWeight',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: false,
            props: {
              type: 'number',
              placeholder: 'Net Shipping − Supplementary (kg)',
              variant: 'outlined',
              fullWidth: true,
              readOnly: true,
              sx: {
                backgroundColor: '#E8E8E8',
                '& .MuiOutlinedInput-root.Mui-disabled': {
                  backgroundColor: '#E8E8E8',
                  color: '#999',
                },
              },
            },
          },
          {
            label: 'Component Info Trims Excluded',
            name: 'tcComponentInfoTrimsExcluded',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoTrimsExcluded,
            props: {
              placeholder: 'Enter Trims Excluded Info',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Associated TC',
            name: 'tcComponentInfoAssociatedTc',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoAssociatedTc,
            props: {
              placeholder: 'Enter Associated TC',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fiber Length',
            name: 'tcComponentInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoFiberLength,
            props: {
              placeholder: 'Enter Fiber Length',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Yarn Count',
            name: 'tcComponentInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoYarnCount,
            props: {
              placeholder: 'Enter Yarn Count',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fabric Weight And Construct (kg)',
            name: 'tcComponentInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoFabricWeightAndConstruct,
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fabric Weight Of Finished Product (kg)',
            name: 'tcComponentInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoFabricWeightOfFinishedProduct,
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Additional',
            name: 'tcComponentInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoAdditional,
            props: {
              placeholder: 'Enter Additional Info',
              variant: 'outlined',
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      console.log(values,"values")
      setComponentData(values);
      toggleDrawer();
      // resetForm();
    },
  };

  return (
    <>
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default ComponentInfo;
